import React, { useState } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import { endSurvey } from '../actions/survey/end-survey';

import { UiButton } from '../../ui/ui-button';
import { surveysRoute } from '../../web-routes';

import { CreateSurveyDialog } from './create-survey-dialog';

import { HTMLStripper } from '../../util/html-stripper';
import { confirm } from '../../widgets/confirmation-dialog';

const DEFAULT_PAGE_SIZE = 5;

type InputProps = {
  orgId: number;
  surveys: Array<Survey>;
  hasAllEmployeesPermission: boolean;
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    endSurvey: (surveyId: number) => dispatch(endSurvey(surveyId)),
  },
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

export const EmployeeOnboardingExitSurveyTable = connect(
  null,
  mapDispatchToProps,
)(({ orgId, surveys, hasAllEmployeesPermission, actions }: Props) => {
  const [offset, setOffset] = useState<number>(0);
  const [pageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [isAddingEmployee, setIsAddingEmployee] = useState<boolean>(false);

  const handleAddEmployeeClick = () => setIsAddingEmployee(!isAddingEmployee);
  const handleAddEmployeeClose = () => setIsAddingEmployee(false);

  const changePage = (_event: any, page: number) => {
    setOffset(page * pageSize);
  };

  const handleEndSurveyClick = (surveyToEndId: number) => async () => {
    if (await confirm('Ending a survey prevents future responses.')) {
      actions.endSurvey(surveyToEndId);
    }
  };

  return (
    <>
      <Table className="u-margin-bottom-md">
        <TableHead>
          <TableRow>
            <TableCell>Survey Question</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {surveys.slice(offset, offset + pageSize).map((row) => {
            const cleanMessage = HTMLStripper.strip(row.messageBody) || '--';
            const isSurveyRunning = moment.utc().isBetween(row.sentAt, row.endAt);
            return (
              <React.Fragment key={row.surveyId}>
                <TableRow>
                  <TableCell size="small" data-cy="onboarding-exit-survey-container">
                    <div className="u-spread">
                      <div>
                        {cleanMessage.length > 200
                          ? `${cleanMessage.slice(0, 200)}...`
                          : cleanMessage}
                      </div>
                      <div className="u-flexShrink-0">
                        {hasAllEmployeesPermission && (
                          <div className="u-display-inlineBlock">
                            <UiButton
                              className="u-margin-left-md u-margin-right-md"
                              variant="secondary"
                              onClick={handleAddEmployeeClick}
                            >
                              Add Employees
                            </UiButton>
                          </div>
                        )}
                        {isSurveyRunning ? (
                          <div className="u-display-inlineBlock">
                            <UiButton
                              className="u-margin-right-md"
                              variant="secondary"
                              onClick={handleEndSurveyClick(row.surveyId)}
                            >
                              End Survey
                            </UiButton>
                          </div>
                        ) : null}
                        <div className="u-display-inlineBlock">
                          <UiButton linkTo={surveysRoute(orgId, row.surveyId)} variant="primary">
                            View Results
                          </UiButton>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
                {isAddingEmployee ? (
                  <div className="u-flexShrink-0">
                    <CreateSurveyDialog
                      orgId={orgId}
                      surveyId={row.surveyId}
                      onClose={handleAddEmployeeClose}
                      surveyType={row.surveyType}
                      isAddingEmployee
                    />
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      {surveys.length >= pageSize && (
        <TablePagination
          component="div"
          count={surveys.length}
          rowsPerPage={pageSize}
          page={offset / pageSize}
          backIconButtonProps={{ 'aria-label': 'Previous Page' }}
          nextIconButtonProps={{ 'aria-label': 'Next Page' }}
          onPageChange={changePage}
          rowsPerPageOptions={[pageSize]}
        />
      )}
    </>
  );
});
